import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { toast } from 'sonner'
import z from 'zod'

import { Form } from '@/components/block/tanstack-form'
import { ButtonLoading } from '@/components/ui/button'
import { InputPassword } from '@/components/ui/input-password'

import { authLogin } from '@/features/auth/api'

import { useAppForm } from '@/hook/form-hook'
import { login } from '@/lib/auth'

export const Route = createFileRoute('/auth/login')({
  head: () => ({
    meta: [{ title: 'IKKI Pos | Login' }],
  }),
  component: Component,
})

const FormDto = z.object({
  email: z.email(),
  password: z.string().min(3),
})

type FormDto = z.infer<typeof FormDto>

function Component() {
  const router = useRouter()
  const navigate = Route.useNavigate()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (data: FormDto) => {
      const res = await authLogin.fetch({ body: data })
      const { token, account } = res.data
      await login(token)
      await router.invalidate()
      navigate({ to: '/', replace: true })
      return { name: account.name }
    },
  })

  const form = useAppForm({
    defaultValues: getDefaultValues() as FormDto,
    validators: { onSubmit: FormDto },
    onSubmit: async ({ value, formApi }) => {
      await toast
        .promise(mutateAsync(value), {
          loading: 'Mencoba masuk...',
          success: data => `Selamat datang ${data.name}`,
          error: err => {
            let msg = err.message

            if (err.code === 'EMAIL_NOT_FOUND' || err.code === 'INVALID_PASSWORD') {
              msg = 'Email atau password salah'
            }

            formApi.setFieldValue('password', '')
            return msg
          },
        })
        .unwrap()
    },
  })

  return (
    <form.AppForm>
      <Form className='inline-flex w-full max-w-xs flex-col'>
        <div className='mb-4 flex w-full flex-col items-center text-center'>
          <h1 className='text-xl font-semibold tracking-wide'>IKKI Smart Management</h1>
          <p className='text-muted-foreground'>Masuk untuk mengakses</p>
        </div>
        <div className='mt-4 flex w-full flex-col gap-y-4'>
          <form.AppField name='email'>
            {field => (
              <field.Wrapper label='Email'>
                <field.Input
                  type='email'
                  autoComplete='email'
                  placeholder='Contoh: admin@ikki.com'
                  required
                />
              </field.Wrapper>
            )}
          </form.AppField>
          <form.AppField name='password'>
            {field => (
              <field.Wrapper label='Password'>
                <field.Control>
                  <InputPassword
                    autoComplete='current-password'
                    placeholder='Password'
                    value={field.state.value}
                    onChange={v => field.handleChange(v.target.value)}
                    onBlur={field.handleBlur}
                    required
                    minLength={3}
                  />
                </field.Control>
              </field.Wrapper>
            )}
          </form.AppField>
          <ButtonLoading className='mt-2 w-full' type='submit' isLoading={isPending}>
            Masuk
          </ButtonLoading>
        </div>
      </Form>
    </form.AppForm>
  )
}

function getDefaultValues(): FormDto {
  return {
    email: '',
    password: '',
  }
}
